import { useEffect, useMemo, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import AdminShell from './AdminShell'
import Toggle from '../../components/Toggle'
import MapPicker from '../../components/MapPicker'
import CoursePhotoModal from '../../components/CoursePhotoModal'
import { tournamentApi } from '../../services/api'
import { useLanguage } from '../../context/LanguageContext'

/**
 * Course Integrations — top-level: table of every course in the InteGolf
 * catalog with which integration powers it + how many resellers are using it.
 *
 * "Add New Course" routes to the provider picker (/admin/course-integrations/add)
 * → provider detail (/admin/course-integrations/:provider) where the actual
 * course-add + rate-set lives.
 *
 * Inline "Edit rate" opens the rate-picker modal directly without going through
 * the provider detail page.
 */
export default function CourseIntegrations() {
  const [rows, setRows] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [search, setSearch] = useState('')
  const [providerFilter, setProviderFilter] = useState('')
  const [provinceFilter, setProvinceFilter] = useState('')
  const [rateFilter, setRateFilter] = useState('all') // all | set | not_set
  const [bookableFilter, setBookableFilter] = useState('bookable') // bookable | all
  const [activeFilter, setActiveFilter] = useState('active') // all | active | inactive — default to admin-approved only
  const [locationFilter, setLocationFilter] = useState('all') // all | missing
  const [packageFilter, setPackageFilter] = useState('all') // all | in | out
  const [editing, setEditing] = useState(null)
  const [editingLocation, setEditingLocation] = useState(null)
  const [editingImage, setEditingImage] = useState(null)
  const [managing, setManaging] = useState(null) // { courseId, courseName } for the integrations modal
  // Pagination — slice the (already-filtered) list before rendering. The
  // network fetch returns the full catalog; the table is what's slow because
  // every row renders a Toggle + MarkupInput. 10 rows by default keeps the
  // first paint cheap; the size dropdown lets admins widen on demand.
  const [pageSize, setPageSize] = useState(10)
  const [page, setPage] = useState(1)

  // Local rows (already imported / linked) are always considered bookable —
  // only TTC catalog virtual rows carry the explicit isBookable=false flag.
  function isBookable(r) {
    return r.isBookable !== false
  }

  // Extract province from city like "Sydney, Nova Scotia" → "Nova Scotia".
  // Falls back to the explicit province field on virtual TTC rows.
  function provinceOf(r) {
    if (r.province) return r.province
    if (!r.city) return null
    const parts = r.city.split(',').map(s => s.trim()).filter(Boolean)
    return parts.length > 1 ? parts[parts.length - 1] : null
  }

  function reload() {
    setLoading(true); setError(null)
    tournamentApi.get('/admin/course-integrations')
      .then(r => setRows(r.data.rows || []))
      .catch(err => setError(err?.response?.data?.message || err.message))
      .finally(() => setLoading(false))
  }
  useEffect(reload, [])

  // tee-times.com catalog rows import with one click — prices are inline, so
  // there's no rate to pick (unlike TTC, which opens the RatePickerModal).
  const [importingOrg, setImportingOrg] = useState(null)
  async function importTeetimeCom(r) {
    setImportingOrg(r.ttOrganizationId)
    try {
      await tournamentApi.post('/admin/course-integrations/import-teetime-com', { ttOrganizationId: r.ttOrganizationId })
      reload()
    } catch (err) {
      alert(err?.response?.data?.message || err.message)
    } finally {
      setImportingOrg(null)
    }
  }

  const providers = useMemo(() => {
    const set = new Set()
    rows.forEach(r => r.integration?.provider && set.add(r.integration.provider))
    return Array.from(set).sort()
  }, [rows])

  const provinces = useMemo(() => {
    const set = new Set()
    rows.forEach(r => { const p = provinceOf(r); if (p) set.add(p) })
    return Array.from(set).sort()
  }, [rows])

  const filtered = useMemo(() => {
    let arr = rows
    if (bookableFilter === 'bookable') arr = arr.filter(isBookable)
    if (activeFilter === 'active')   arr = arr.filter(r => r.isResellerActive === true)
    if (activeFilter === 'inactive') arr = arr.filter(r => r.isResellerActive === false && !r.isVirtual)
    if (locationFilter === 'missing') arr = arr.filter(r => !r.isVirtual && !r.hasLocation)
    if (packageFilter === 'in')  arr = arr.filter(r => (r.packageCount || 0) > 0)
    if (packageFilter === 'out') arr = arr.filter(r => !r.isVirtual && (r.packageCount || 0) === 0)
    if (providerFilter) arr = arr.filter(r => r.integration?.provider === providerFilter)
    if (provinceFilter) arr = arr.filter(r => provinceOf(r) === provinceFilter)
    // "Set" includes courses where the rate concept doesn't apply
    // (TeeOn etc. — pricing is inline). "Not selected" is only courses
    // where the admin actually needs to pick something.
    if (rateFilter === 'set')     arr = arr.filter(r => r.integration?.hasRate || r.integration?.ratesAreInline)
    if (rateFilter === 'not_set') arr = arr.filter(r => r.integration && !r.integration.hasRate && !r.integration.ratesAreInline)
    if (search) {
      const s = search.toLowerCase()
      arr = arr.filter(r =>
        (r.courseName || '').toLowerCase().includes(s) ||
        (r.city || '').toLowerCase().includes(s)
      )
    }
    return arr
  }, [rows, search, providerFilter, provinceFilter, rateFilter, bookableFilter, activeFilter, locationFilter, packageFilter])

  // Reset to page 1 any time the filtered set shrinks/changes so we don't end
  // up on a page that no longer exists.
  useEffect(() => { setPage(1) }, [search, providerFilter, provinceFilter, rateFilter, bookableFilter, activeFilter, locationFilter, packageFilter, pageSize])

  const totalFiltered = filtered.length
  const totalPages = pageSize === 'all' ? 1 : Math.max(1, Math.ceil(totalFiltered / pageSize))
  const safePage = Math.min(page, totalPages)
  const paged = useMemo(() => {
    if (pageSize === 'all') return filtered
    const start = (safePage - 1) * pageSize
    return filtered.slice(start, start + pageSize)
  }, [filtered, safePage, pageSize])
  const pageStartIdx = pageSize === 'all' || totalFiltered === 0 ? 0 : (safePage - 1) * pageSize + 1
  const pageEndIdx = pageSize === 'all' ? totalFiltered : Math.min(safePage * pageSize, totalFiltered)

  const locationCounts = useMemo(() => {
    const base = bookableFilter === 'bookable' ? rows.filter(isBookable) : rows
    const localOnly = base.filter(r => !r.isVirtual)
    return {
      all:     base.length,
      missing: localOnly.filter(r => !r.hasLocation).length,
    }
  }, [rows, bookableFilter])

  const activeCounts = useMemo(() => {
    const base = bookableFilter === 'bookable' ? rows.filter(isBookable) : rows
    return {
      all:      base.length,
      active:   base.filter(r => r.isResellerActive === true).length,
      inactive: base.filter(r => r.isResellerActive === false && !r.isVirtual).length,
    }
  }, [rows, bookableFilter])

  const localCount   = rows.filter(r => !r.isVirtual).length
  const virtualCount = rows.filter(r => r.isVirtual).length
  // Imported courses whose coordinates are only approximate (e.g. a city-level
  // tee-times.com import) — a super admin should drop a precise pin.
  const approxCount  = rows.filter(r => !r.isVirtual && r.locationApprox).length

  // Counts that drive the rate quick-filter chips. Scoped by bookable
  // filter so the numbers match the visible table. Inline-pricing
  // providers count as "set" — there's no rate to pick.
  const rateCounts = useMemo(() => {
    const base = bookableFilter === 'bookable' ? rows.filter(isBookable) : rows
    return {
      all:     base.length,
      set:     base.filter(r => r.integration?.hasRate || r.integration?.ratesAreInline).length,
      not_set: base.filter(r => r.integration && !r.integration.hasRate && !r.integration.ratesAreInline).length,
    }
  }, [rows, bookableFilter])

  const bookableCounts = useMemo(() => ({
    bookable:    rows.filter(isBookable).length,
    nonBookable: rows.filter(r => !isBookable(r)).length,
    all:         rows.length,
  }), [rows])

  // Counts for the "In a package" quick-filter chips. Scoped by the bookable
  // filter like the others so the numbers match the visible table.
  const packageCounts = useMemo(() => {
    const base = bookableFilter === 'bookable' ? rows.filter(isBookable) : rows
    return {
      all: base.length,
      in:  base.filter(r => (r.packageCount || 0) > 0).length,
      out: base.filter(r => !r.isVirtual && (r.packageCount || 0) === 0).length,
    }
  }, [rows, bookableFilter])

  return (
    <AdminShell title="Course Integrations">
      <p style={{ color: '#64748b', margin: '0 0 20px', maxWidth: 720, lineHeight: 1.5 }}>
        Every course in the InteGolf catalog and which tee-sheet provider it's connected through.
        Click <b>Edit</b> to change the default rate. Click <b>Add new course</b> to pick a provider
        and connect a new course.
      </p>

      <GolfTrailImagePanel onSynced={reload} />

      {approxCount > 0 && (
        <div style={{ margin: '0 0 16px', padding: '10px 14px', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 10, fontSize: 13, color: '#92400e', display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <span style={{ fontWeight: 700 }}>📍 {approxCount} course{approxCount === 1 ? '' : 's'} with an approximate location.</span>
          <span>These were imported with only a city-level pin (e.g. from tee-times.com). Drop a precise pin so grouping radii and distances are accurate — look for the <b style={{ background: '#fef3c7', borderRadius: 10, padding: '1px 6px' }}>📍 approx</b> tag in the Location column.</span>
        </div>
      )}

      {/* Quick filter chips: bookable status (default = bookable) */}
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 8, flexWrap: 'wrap' }}>
        <span style={{ fontSize: 12, color: '#64748b', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5, marginRight: 4, minWidth: 75 }}>Bookable:</span>
        <Chip active={bookableFilter === 'bookable'} onClick={() => setBookableFilter('bookable')}>
          Bookable ({bookableCounts.bookable})
        </Chip>
        <Chip active={bookableFilter === 'all'} onClick={() => setBookableFilter('all')}>
          All ({bookableCounts.all})
        </Chip>
        {bookableFilter === 'all' && bookableCounts.nonBookable > 0 && (
          <span style={{ fontSize: 12, color: '#94a3b8' }}>
            includes {bookableCounts.nonBookable} non-bookable
          </span>
        )}
      </div>

      {/* Quick filter chips: active status */}
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 8, flexWrap: 'wrap' }}>
        <span style={{ fontSize: 12, color: '#64748b', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5, marginRight: 4, minWidth: 75 }}>Active:</span>
        <Chip active={activeFilter === 'all'}      onClick={() => setActiveFilter('all')}>All ({activeCounts.all})</Chip>
        <Chip active={activeFilter === 'active'}   onClick={() => setActiveFilter('active')}>Active ({activeCounts.active})</Chip>
        <Chip active={activeFilter === 'inactive'} onClick={() => setActiveFilter('inactive')}>Hidden ({activeCounts.inactive})</Chip>
      </div>

      {/* Quick filter chips: rate status */}
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 8, flexWrap: 'wrap' }}>
        <span style={{ fontSize: 12, color: '#64748b', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5, marginRight: 4, minWidth: 75 }}>Rate:</span>
        <Chip active={rateFilter === 'all'}     onClick={() => setRateFilter('all')}>All ({rateCounts.all})</Chip>
        <Chip active={rateFilter === 'set'}     onClick={() => setRateFilter('set')}>Selected ({rateCounts.set})</Chip>
        <Chip active={rateFilter === 'not_set'} onClick={() => setRateFilter('not_set')}>Not selected ({rateCounts.not_set})</Chip>
      </div>

      {/* Quick filter chips: location status */}
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 12, flexWrap: 'wrap' }}>
        <span style={{ fontSize: 12, color: '#64748b', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5, marginRight: 4, minWidth: 75 }}>Location:</span>
        <Chip active={locationFilter === 'all'}     onClick={() => setLocationFilter('all')}>All ({locationCounts.all})</Chip>
        <Chip active={locationFilter === 'missing'} onClick={() => setLocationFilter('missing')}>Missing ({locationCounts.missing})</Chip>
      </div>

      {/* Quick filter chips: package membership */}
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 12, flexWrap: 'wrap' }}>
        <span style={{ fontSize: 12, color: '#64748b', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5, marginRight: 4, minWidth: 75 }}>Packages:</span>
        <Chip active={packageFilter === 'all'} onClick={() => setPackageFilter('all')}>All ({packageCounts.all})</Chip>
        <Chip active={packageFilter === 'in'}  onClick={() => setPackageFilter('in')}>In a package ({packageCounts.in})</Chip>
        <Chip active={packageFilter === 'out'} onClick={() => setPackageFilter('out')}>Not in a package ({packageCounts.out})</Chip>
      </div>

      <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 16, flexWrap: 'wrap' }}>
        <input
          placeholder="Search course or city…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={inputStyle}
        />
        <select value={provinceFilter} onChange={(e) => setProvinceFilter(e.target.value)} style={inputStyle}>
          <option value="">All provinces</option>
          {provinces.map(p => <option key={p} value={p}>{p}</option>)}
        </select>
        <select value={providerFilter} onChange={(e) => setProviderFilter(e.target.value)} style={inputStyle}>
          <option value="">All providers</option>
          {providers.map(p => <option key={p} value={p}>{p}</option>)}
        </select>
        {(providerFilter || provinceFilter || rateFilter !== 'all' || bookableFilter !== 'bookable' || activeFilter !== 'active' || locationFilter !== 'all' || packageFilter !== 'all' || search) && (
          <button
            onClick={() => { setSearch(''); setProviderFilter(''); setProvinceFilter(''); setRateFilter('all'); setBookableFilter('bookable'); setActiveFilter('active'); setLocationFilter('all'); setPackageFilter('all') }}
            style={{ ...smallBtn, color: '#64748b' }}
          >Clear</button>
        )}
        <Link to="/admin/course-integrations/add" style={{ ...primaryBtn, marginLeft: 'auto', textDecoration: 'none', display: 'inline-block' }}>
          + Add new course
        </Link>
      </div>

      {error && <div style={errorBoxStyle}>{error}</div>}

      <ZeroPriceWorklist onManage={setManaging} />

      <DuplicatesPanel onManage={setManaging} onChanged={reload} />

      {!loading && (
        <div style={{ fontSize: 13, color: '#64748b', marginBottom: 12 }}>
          <b>{localCount}</b> imported · <b>{virtualCount}</b> available in provider catalog (not yet imported)
        </div>
      )}

      {loading ? (
        <div style={{ padding: 40, textAlign: 'center', color: '#64748b' }}>Loading…</div>
      ) : filtered.length === 0 ? (
        <div style={{ padding: 40, textAlign: 'center', color: '#64748b', background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8 }}>
          No courses match. Click <b>+ Add new course</b> to connect one.
        </div>
      ) : (
        <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8, overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead style={{ background: '#f8fafc' }}>
              <tr>
                <Th style={{ textAlign: 'center', width: 80 }}>Active</Th>
                <Th>Course</Th>
                <Th>Location</Th>
                <Th>Provider</Th>
                <Th>Default rate</Th>
                <Th style={{ textAlign: 'center', width: 100 }}>Markup</Th>
                <Th style={{ textAlign: 'center' }}>Resellers</Th>
                <Th style={{ textAlign: 'center' }}>Packages</Th>
                <Th style={{ width: 1 }}></Th>
              </tr>
            </thead>
            <tbody>
              {paged.map(r => {
                const nonBookable = !isBookable(r)
                return (
                <tr
                  key={r.isVirtual ? (r.virtualSource === 'teetime-com' ? `ttorg-${r.ttOrganizationId}` : `ttc-${r.ttcCourseCode}`) : `course-${r.courseId}`}
                  style={{
                    borderTop: '1px solid #f1f5f9',
                    background: nonBookable ? '#f8fafc' : (r.isVirtual ? '#fafbff' : 'transparent'),
                    opacity: nonBookable ? 0.7 : 1,
                  }}
                >
                  <Td style={{ textAlign: 'center' }}>
                    {r.isVirtual ? (
                      <span style={{ fontSize: 11, color: '#cbd5e1' }} title="Import the course first to toggle availability">—</span>
                    ) : (
                      <ActiveToggle
                        coursesId={r.courseId}
                        active={r.isResellerActive}
                        disabled={nonBookable}
                        onChange={(next) => {
                          // Optimistic update
                          setRows(prev => prev.map(x => x.courseId === r.courseId ? { ...x, isResellerActive: next } : x))
                        }}
                      />
                    )}
                  </Td>
                  <Td>
                    <div style={{ fontWeight: 500, color: '#0f172a' }}>
                      {r.courseName || '(unnamed)'}
                      {nonBookable && <span style={{ ...pill('#f1f5f9', '#475569'), marginLeft: 8 }}>Non-bookable</span>}
                    </div>
                    {r.isVirtual ? (
                      <div style={{ fontSize: 11, color: '#7c3aed' }}>📦 In provider catalog · {r.virtualSource === 'teetime-com' ? `tee-times.com · org ${r.ttOrganizationId}` : r.ttcCourseCode}</div>
                    ) : (
                      <div style={{ fontSize: 11, color: '#94a3b8' }}>
                        #{r.courseId}
                        {r.isPlatformManaged && <span style={{ ...pill('#fef3c7', '#92400e'), marginLeft: 6 }}>🔒 InteGolf</span>}
                      </div>
                    )}
                    {!r.isVirtual && (
                      r.imageUrl ? (
                        <button
                          type="button"
                          onClick={() => setEditingImage(r)}
                          title="Course photo — click to change, replace, or clear"
                          style={{ marginTop: 5, display: 'inline-flex', alignItems: 'center', gap: 6, padding: 0, border: 'none', background: 'none', cursor: 'pointer', fontFamily: 'inherit' }}
                        >
                          <img
                            src={r.imageUrl}
                            alt=""
                            style={{ width: 46, height: 32, objectFit: 'cover', borderRadius: 5, border: '1px solid #e2e8f0', display: 'block', background: '#f1f5f9' }}
                            onError={(e) => { e.currentTarget.style.opacity = 0.25 }}
                          />
                          <span style={{
                            fontSize: 9.5, fontWeight: 700, padding: '2px 6px', borderRadius: 8, whiteSpace: 'nowrap',
                            background: r.imageSource === 'golftrail' ? '#dcfce7' : '#e0e7ff',
                            color: r.imageSource === 'golftrail' ? '#166534' : '#3730a3',
                          }}>{r.imageSource === 'golftrail' ? 'Golf Trail' : 'Custom'}</span>
                        </button>
                      ) : (
                        <button
                          type="button"
                          onClick={() => setEditingImage(r)}
                          title="No scenic photo — customers see the logo. Add a hero photo shown on the trip page golf rows."
                          style={{
                            marginTop: 4, padding: '2px 8px', fontSize: 10, fontWeight: 600,
                            background: '#f1f5f9', color: '#64748b',
                            border: 'none', borderRadius: 10, cursor: 'pointer', fontFamily: 'inherit',
                          }}
                        >🖼 Add image</button>
                      )
                    )}
                  </Td>
                  <Td>
                    {r.isVirtual ? (
                      r.city || <span style={{ color: '#cbd5e1' }}>—</span>
                    ) : r.hasLocation ? (
                      <span>
                        {r.city || <span style={{ color: '#cbd5e1' }}>—</span>}
                        {!r.hasCoords && (
                          <button
                            onClick={() => setEditingLocation(r)}
                            title="City is set but no map coordinates — resellers' grouping radius queries will miss this course"
                            style={{ marginLeft: 6, padding: '2px 6px', fontSize: 10, fontWeight: 600, background: '#fef3c7', color: '#92400e', border: 'none', borderRadius: 10, cursor: 'pointer' }}
                          >📍 no pin</button>
                        )}
                        {r.hasCoords && r.locationApprox && (
                          <button
                            onClick={() => setEditingLocation(r)}
                            title="Imported with only an approximate (city-level) location — drop a precise pin so groupings and distances are accurate"
                            style={{ marginLeft: 6, padding: '2px 6px', fontSize: 10, fontWeight: 600, background: '#fef3c7', color: '#92400e', border: 'none', borderRadius: 10, cursor: 'pointer' }}
                          >📍 approx</button>
                        )}
                      </span>
                    ) : (
                      <button
                        type="button"
                        onClick={() => setEditingLocation(r)}
                        title="No city or coordinates — resellers can't include this course in radius-based groupings until set"
                        style={{
                          padding: '4px 10px', background: '#fee2e2', color: '#991b1b',
                          borderRadius: 12, fontSize: 11, fontWeight: 600,
                          border: 'none', cursor: 'pointer', fontFamily: 'inherit',
                        }}
                      >📍 Set location</button>
                    )}
                  </Td>
                  <Td>
                    {r.integration?.provider ? (
                      // Always the clean two-bucket label (InteGolf / Tee-Time.com),
                      // with the tee-sheet backend appended (e.g. "InteGolf · TTC").
                      <Link to={`/admin/course-integrations/${r.integration.provider}`} style={{ textDecoration: 'none' }}>
                        <span style={{
                          ...pill(r.integration.providerClass === 'apex' ? '#fef3c7' : '#dcfce7',
                                  r.integration.providerClass === 'apex' ? '#92400e' : '#166534'),
                          cursor: 'pointer',
                        }}>
                          {r.integration.providerClassName || r.integration.provider}
                          {r.integration.providerBackend ? <span style={{ opacity: 0.7, fontWeight: 400 }}> · {r.integration.providerBackend}</span> : null}
                        </span>
                      </Link>
                    ) : <span style={{ color: '#cbd5e1' }}>—</span>}
                  </Td>
                  <Td>
                    {!r.integration ? (
                      <span style={{ color: '#cbd5e1' }}>(no integration)</span>
                    ) : r.integration.ratesAreInline ? (
                      <span title="Pricing is returned inline per tee time — no rate code to select for this provider"
                            style={{ fontSize: 13, color: '#94a3b8' }}>
                        N/A · inline pricing
                      </span>
                    ) : !r.integration.hasRate ? (
                      r.integration.suggestion?.rateName ? (
                        <span style={{ fontSize: 13, color: '#64748b' }}>
                          <span style={{ color: '#f59e0b' }}>★ </span>
                          Suggested: {r.integration.suggestion.rateName}
                        </span>
                      ) : r.isVirtual ? (
                        <span style={{ fontSize: 13, color: '#94a3b8' }}>(set on import)</span>
                      ) : (
                        <span style={pill('#fee2e2', '#991b1b')}>missing</span>
                      )
                    ) : (
                      <span>
                        {r.integration.currentLooksPublic ? <span style={{ color: '#f59e0b' }}>★ </span> : null}
                        <code style={{ fontSize: 12, color: '#0f172a' }}>{r.integration.rateName || r.integration.rateCode}</code>
                      </span>
                    )}
                  </Td>
                  <Td style={{ textAlign: 'center' }}>
                    {r.isVirtual ? (
                      <span style={{ color: '#cbd5e1', fontSize: 11 }}>—</span>
                    ) : (
                      <MarkupInput
                        coursesId={r.courseId}
                        value={r.golfMarkupPct}
                        custom={r.golfMarkupCustom}
                        onChanged={(next) => {
                          setRows(prev => prev.map(x => x.courseId === r.courseId
                            ? { ...x, golfMarkupPct: next, golfMarkupCustom: true } : x))
                        }}
                      />
                    )}
                  </Td>
                  <Td style={{ textAlign: 'center', color: '#64748b', fontVariantNumeric: 'tabular-nums' }}>
                    {r.isVirtual ? <span style={{ color: '#cbd5e1' }}>—</span> : r.attachedResellers}
                  </Td>
                  <Td style={{ textAlign: 'center', fontVariantNumeric: 'tabular-nums' }}>
                    {r.isVirtual ? <span style={{ color: '#cbd5e1' }}>—</span> : <PackagesCell count={r.packageCount || 0} packages={r.packages || []} />}
                  </Td>
                  <Td>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6, alignItems: 'flex-start' }}>
                      {r.integration && !nonBookable && (r.isVirtual || !r.integration.ratesAreInline) && (
                        <button
                          onClick={() => (r.isVirtual && r.virtualSource === 'teetime-com') ? importTeetimeCom(r) : setEditing(r)}
                          disabled={r.isVirtual && r.virtualSource === 'teetime-com' && importingOrg === r.ttOrganizationId}
                          style={smallBtn}>
                          {r.isVirtual
                            ? (r.virtualSource === 'teetime-com' && importingOrg === r.ttOrganizationId ? 'Importing…' : 'Import')
                            : 'Edit'}
                        </button>
                      )}
                      {!r.isVirtual && (
                        <button
                          onClick={() => setManaging({ courseId: r.courseId, courseName: r.courseName })}
                          style={{ background: 'none', border: 'none', color: '#2563eb', fontSize: 12, cursor: 'pointer', padding: 0, textAlign: 'left', whiteSpace: 'nowrap' }}
                        >
                          Integrations
                        </button>
                      )}
                    </div>
                  </Td>
                </tr>
              )})}
            </tbody>
          </table>
          <Pager
            startIdx={pageStartIdx}
            endIdx={pageEndIdx}
            total={totalFiltered}
            page={safePage}
            totalPages={totalPages}
            pageSize={pageSize}
            onPageChange={setPage}
            onPageSizeChange={setPageSize}
          />
        </div>
      )}

      {editing && (
        <RatePickerModal
          row={editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); reload() }}
        />
      )}
      {editingLocation && (
        <LocationModal
          row={editingLocation}
          onClose={() => setEditingLocation(null)}
          onSaved={() => { setEditingLocation(null); reload() }}
        />
      )}
      {editingImage && (
        <CoursePhotoModal
          courseId={editingImage.courseId}
          courseName={editingImage.courseName}
          city={editingImage.city}
          imageUrl={editingImage.imageUrl || ''}
          imagesUrl={`/admin/course-integrations/courses/${editingImage.courseId}/images`}
          uploadUrl="/admin/course-integrations/upload-image"
          uploadField="file"
          saveUrl={`/admin/course-integrations/courses/${editingImage.courseId}/image`}
          allowClear
          onClose={() => setEditingImage(null)}
          onSaved={({ imageUrl }) => {
            // Update just this row in place — no full reload, so the page
            // doesn't jump back to the top / lose scroll position.
            const cid = editingImage.courseId
            setRows(prev => prev.map(x => x.courseId === cid
              ? { ...x, imageUrl: imageUrl || null, imageSource: imageUrl ? 'manual' : null }
              : x))
            setEditingImage(null)
          }}
        />
      )}
      {managing && (
        <IntegrationsModal
          course={managing}
          onClose={() => setManaging(null)}
        />
      )}
    </AdminShell>
  )
}

/**
 * IntegrationsModal — show every integration_providers row wired to a course
 * so an admin can diagnose "no tee times" (e.g. multiple active golf-booking
 * integrations where the router picks a stale one) and toggle the wrong ones
 * off. Secrets are masked; courseCode / baseUrl / division codes are shown in
 * full since those are the usual culprits.
 */
function IntegrationsModal({ course, onClose }) {
  const [data, setData] = useState(null)
  const [err, setErr] = useState(null)
  const [showReplace, setShowReplace] = useState(false)
  const [replaceText, setReplaceText] = useState('')
  const [replaceBusy, setReplaceBusy] = useState(false)
  const [testDate, setTestDate] = useState(() => new Date(Date.now() + 14 * 86400000).toISOString().slice(0, 10))
  const [testGolfers, setTestGolfers] = useState(4)
  const [testResult, setTestResult] = useState(null)
  const [testBusy, setTestBusy] = useState(false)

  function load() {
    setErr(null)
    tournamentApi.get(`/admin/course-integrations/courses/${course.courseId}/integrations`)
      .then(r => setData(r.data))
      .catch(e => setErr(e?.response?.data?.message || e.message))
  }
  useEffect(load, [course.courseId])

  async function applyReplace() {
    let payload
    try { payload = JSON.parse(replaceText) }
    catch { alert('That is not valid JSON.'); return }
    if (payload.coursesId != null && Number(payload.coursesId) !== Number(course.courseId)) {
      alert(`This JSON is for course #${payload.coursesId}, but you're editing #${course.courseId}. Paste the JSON for this course.`)
      return
    }
    if (!confirm(`Replace ALL golf-booking integrations for "${course.courseName}" (#${course.courseId})? This removes the existing ones.`)) return
    setReplaceBusy(true)
    try {
      const { data: res } = await tournamentApi.post(`/admin/course-integrations/courses/${course.courseId}/replace-integration`, payload)
      alert(`Done — removed ${res.removed} old, installed ${res.created.length} new.`)
      setReplaceText(''); setShowReplace(false); load()
    } catch (e) {
      alert(e?.response?.data?.message || e.message)
    } finally { setReplaceBusy(false) }
  }

  // Hand the full diagnostic (picked provider, integrations, raw pre-guard probe,
  // rate names, sample rows, guard verdict, diagnosis) to a JSON file so it can
  // be attached for troubleshooting instead of screenshotted.
  function downloadDiagnostics() {
    const payload = {
      generatedAt: new Date().toISOString(),
      courseId: course.courseId, courseName: course.courseName,
      date: testDate, golfers: testGolfers, result: testResult,
    }
    const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url; a.download = `course-${course.courseId}-diagnostics-${testDate}.json`
    document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url)
  }

  async function runTest() {
    setTestBusy(true); setTestResult(null)
    try {
      const { data: r } = await tournamentApi.get(`/admin/course-integrations/courses/${course.courseId}/test-search`, { params: { date: testDate, golfers: testGolfers } })
      setTestResult(r)
    } catch (e) { setTestResult({ error: e?.response?.data?.message || e.message }) }
    finally { setTestBusy(false) }
  }

  const activeGolf = (data?.integrations || []).filter(i => i.isActive && i.category === 'golf_booking')

  return (
    <div style={modalOverlay} onClick={onClose}>
      <div style={{ ...modalBody, maxWidth: 760, maxHeight: '85vh', overflowY: 'auto' }} onClick={e => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
          <h3 style={{ margin: 0, fontSize: 17 }}>Integrations — {course.courseName} <span style={{ color: '#94a3b8', fontWeight: 400 }}>#{course.courseId}</span></h3>
          <button onClick={onClose} style={smallBtn}>Close</button>
        </div>

        {activeGolf.length > 1 && (
          <div style={{ padding: 10, background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 8, fontSize: 12.5, color: '#92400e', lineHeight: 1.5, marginBottom: 12 }}>
            ⚠ <strong>{activeGolf.length} active golf-booking integrations.</strong> The booking router uses one (InteGolf-class first, else first wired) — turn the stale ones off so it picks the working provider.
          </div>
        )}
        {err && <div style={errorBoxStyle}>{err}</div>}

        {!data ? (
          <div style={{ padding: 24, textAlign: 'center', color: '#64748b' }}>Loading…</div>
        ) : data.integrations.length === 0 ? (
          <div style={{ padding: 24, textAlign: 'center', color: '#64748b' }}>No integrations wired to this course.</div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {data.integrations.map(i => <IntegrationCard key={i.id} intg={i} onChanged={load} />)}
          </div>
        )}

        <div style={{ marginTop: 16, borderTop: '1px solid #e2e8f0', paddingTop: 14 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: '#0f172a', marginBottom: 6 }}>Test tee-time search</div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <input type="date" value={testDate} onChange={e => setTestDate(e.target.value)} style={{ padding: '6px 8px', border: '1px solid #cbd5e1', borderRadius: 4, fontSize: 13 }} />
            <label style={{ fontSize: 12, color: '#475569' }}>golfers
              <input type="number" min={1} max={8} value={testGolfers} onChange={e => setTestGolfers(Number(e.target.value) || 1)} style={{ width: 56, padding: '6px 8px', border: '1px solid #cbd5e1', borderRadius: 4, fontSize: 13, marginLeft: 4 }} />
            </label>
            <button onClick={runTest} disabled={testBusy} style={smallBtn}>{testBusy ? 'Testing…' : 'Run test'}</button>
            {testResult && !testResult.error && <button onClick={downloadDiagnostics} style={smallBtn}>Download JSON</button>}
          </div>
          {testResult && (
            <div style={{ marginTop: 8, fontSize: 12, background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 6, padding: 10, fontFamily: 'monospace' }}>
              {testResult.error ? (
                <div style={{ color: '#991b1b' }}>{testResult.error}</div>
              ) : (
                <>
                  <div><strong>Picked:</strong> {testResult.picked?.provider || '—'} <span style={{ color: '#94a3b8' }}>({testResult.picked?.chosenBy || testResult.picked?.error || '?'})</span></div>
                  <div style={{ marginTop: 4 }}><strong>Search:</strong> {testResult.search?.error
                    ? <span style={{ color: '#991b1b' }}>error — {testResult.search.error}</span>
                    : <span style={{ color: testResult.search?.count ? '#166534' : '#b45309' }}>{testResult.search?.count} tee times{testResult.search?.count ? ` (e.g. ${testResult.search.sample.map(s => s.time + ' $' + s.price).join(', ')})` : ''}</span>}</div>

                  {testResult.probe && !testResult.probe.error && (
                    <div style={{ marginTop: 8, fontFamily: 'system-ui, sans-serif' }}>
                      <div style={{
                        padding: '8px 10px', borderRadius: 6, fontSize: 12.5, lineHeight: 1.5,
                        background: testResult.probe.zeroCount === 0 ? '#f0fdf4' : (testResult.probe.pricedCount === 0 ? '#fef2f2' : '#fffbeb'),
                        border: `1px solid ${testResult.probe.zeroCount === 0 ? '#bbf7d0' : (testResult.probe.pricedCount === 0 ? '#fecaca' : '#fde68a')}`,
                        color: testResult.probe.zeroCount === 0 ? '#166534' : (testResult.probe.pricedCount === 0 ? '#991b1b' : '#92400e'),
                      }}>
                        <div><strong>Diagnosis:</strong> {testResult.probe.diagnosis}</div>
                        <div style={{ marginTop: 4, color: '#475569' }}><strong>Guard:</strong> {testResult.probe.guardVerdict}</div>
                        {testResult.probe.rateNames?.length > 0 && (
                          <div style={{ marginTop: 4, color: '#475569' }}><strong>Rate(s) from provider:</strong> {testResult.probe.rateNames.join(', ')}</div>
                        )}
                        <div style={{ marginTop: 4, color: '#64748b', fontSize: 11.5 }}>
                          {testResult.probe.pricedCount} priced · {testResult.probe.zeroCount} at $0 · {testResult.probe.slotCount} total (raw, pre-guard)
                        </div>
                      </div>
                      {testResult.probe.sample?.length > 0 && (
                        <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: 6, fontSize: 11.5 }}>
                          <thead>
                            <tr style={{ color: '#64748b', textAlign: 'left' }}>
                              <th style={{ padding: '2px 6px', fontWeight: 600 }}>Time</th>
                              <th style={{ padding: '2px 6px', fontWeight: 600 }}>Avail</th>
                              <th style={{ padding: '2px 6px', fontWeight: 600 }}>Green fee</th>
                              <th style={{ padding: '2px 6px', fontWeight: 600 }}>Cart</th>
                              <th style={{ padding: '2px 6px', fontWeight: 600 }}>Rate</th>
                            </tr>
                          </thead>
                          <tbody>
                            {testResult.probe.sample.map((r, ri) => (
                              <tr key={ri} style={{ borderTop: '1px solid #eef2f7', color: '#334155' }}>
                                <td style={{ padding: '2px 6px' }}>{r.time || '—'}</td>
                                <td style={{ padding: '2px 6px' }}>{r.availableSlots ?? '—'}</td>
                                <td style={{ padding: '2px 6px', color: (r.greenFee > 0) ? '#166534' : '#b91c1c', fontWeight: 600 }}>{r.greenFee == null ? '—' : '$' + r.greenFee}</td>
                                <td style={{ padding: '2px 6px' }}>{r.cartFee == null ? '—' : '$' + r.cartFee}</td>
                                <td style={{ padding: '2px 6px' }}>{r.rateName || '—'}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      )}
                      <div style={{ marginTop: 6, color: '#94a3b8', fontSize: 11 }}>
                        To fix a $0 course: set a flat green fee on the integration below (makes rounds sellable + clears the alert), or have the course load/expose a public rate.
                      </div>
                    </div>
                  )}

                  <div style={{ marginTop: 6, color: '#64748b' }}>Router sees these integrations:</div>
                  {(testResult.integrations || []).map((i, idx) => (
                    <div key={idx} style={{ paddingLeft: 8, color: '#334155' }}>
                      • {i.provider} <span style={{ color: '#94a3b8' }}>[{i.source}]</span> — code {i.courseCode || '—'}, token {i.facilityToken || '—'}, url {i.baseUrl || '—'}{i.divisionCodes ? `, div ${JSON.stringify(i.divisionCodes)}` : ''}
                    </div>
                  ))}
                </>
              )}
            </div>
          )}
        </div>

        <div style={{ marginTop: 16, borderTop: '1px solid #e2e8f0', paddingTop: 14 }}>
          {!showReplace ? (
            <button onClick={() => setShowReplace(true)} style={smallBtn}>Replace all with JSON…</button>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: '#0f172a' }}>Replace all golf-booking integrations for this course</div>
              <div style={{ fontSize: 12, color: '#64748b' }}>
                Paste the JSON for <strong>#{course.courseId}</strong>. It removes this course's existing golf-booking integrations and installs the pasted one. If the JSON's <code>coursesId</code> doesn't match this course it will be rejected.
              </div>
              <textarea
                value={replaceText}
                onChange={e => setReplaceText(e.target.value)}
                rows={10}
                placeholder={'{ "coursesId": ' + course.courseId + ', "provider": "teeon-gn", "config": { "courseCode": "...", "facilityToken": "..." } }'}
                style={{ width: '100%', boxSizing: 'border-box', fontFamily: 'monospace', fontSize: 12, padding: 10, border: '1px solid #cbd5e1', borderRadius: 6 }}
              />
              <div style={{ display: 'flex', gap: 6 }}>
                <button onClick={applyReplace} disabled={replaceBusy || !replaceText.trim()} style={{ ...smallBtn, background: '#0f172a', color: '#fff', borderColor: '#0f172a', opacity: (replaceBusy || !replaceText.trim()) ? 0.5 : 1 }}>{replaceBusy ? 'Applying…' : 'Apply replacement'}</button>
                <button onClick={() => { setShowReplace(false); setReplaceText('') }} style={smallBtn}>Cancel</button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

/**
 * ZeroPriceWorklist — super-admin banner listing courses whose live tee sheet
 * returned a $0 / unpriced green fee. Those rounds are being HIDDEN from
 * customers/partners (so nothing sells for $0); this surfaces them so the admin
 * can set a flat fee (via the course integrations modal) or dismiss.
 */
function ZeroPriceWorklist({ onManage }) {
  const [events, setEvents] = useState([])
  const [loaded, setLoaded] = useState(false)
  const [showDismissed, setShowDismissed] = useState(false)

  function load() {
    // includeResolved=1 so dismissed entries come back too — Dismiss only HIDES
    // (soft-resolve), the row is kept so it can be re-viewed / restored.
    tournamentApi.get('/admin/course-integrations/zero-price-events', { params: { includeResolved: 1 } })
      .then(r => setEvents(r.data?.events || []))
      .catch(() => setEvents([]))
      .finally(() => setLoaded(true))
  }
  useEffect(load, [])

  async function dismiss(id) {
    try { await tournamentApi.post(`/admin/course-integrations/zero-price-events/${id}/resolve`); load() }
    catch (e) { alert(e?.response?.data?.message || e.message) }
  }
  async function restore(id) {
    try { await tournamentApi.post(`/admin/course-integrations/zero-price-events/${id}/unresolve`); load() }
    catch (e) { alert(e?.response?.data?.message || e.message) }
  }

  if (!loaded) return null
  const active = events.filter(e => !e.resolvedAt)
  const dismissed = events.filter(e => e.resolvedAt)
  if (active.length === 0 && dismissed.length === 0) return null

  const fmtWhen = (d) => { try { return new Date(d).toLocaleString('en-CA', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) } catch { return '' } }

  const rowFor = (e, isDismissed) => (
    <div key={e.id} style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', background: '#fff', border: '1px solid #e2e8f0', borderRadius: 6, padding: '8px 10px', opacity: isDismissed ? 0.75 : 1 }}>
      <div style={{ minWidth: 0, flex: '1 1 260px' }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: '#0f172a' }}>
          {e.courseName || `Course #${e.coursesId}`} <span style={{ color: '#94a3b8', fontWeight: 400 }}>#{e.coursesId}</span>
        </div>
        <div style={{ fontSize: 12, color: '#64748b' }}>
          {e.city ? e.city + ' · ' : ''}{e.providerType || 'unknown'} · {e.slotCount || 0} slot{e.slotCount === 1 ? '' : 's'} · last seen {fmtWhen(e.lastSeenAt)}
          {e.fallbackGreenFee != null && <span style={{ color: '#16a34a', fontWeight: 600 }}> · flat fee ${Number(e.fallbackGreenFee).toFixed(2)} set</span>}
          {isDismissed && <span style={{ color: '#94a3b8' }}> · dismissed {fmtWhen(e.resolvedAt)}</span>}
        </div>
      </div>
      <button onClick={() => onManage({ courseId: e.coursesId, courseName: e.courseName })} style={{ ...smallBtn, background: '#0f172a', color: '#fff', borderColor: '#0f172a' }}>{isDismissed ? 'Re-verify →' : 'Set flat fee →'}</button>
      {isDismissed
        ? <button onClick={() => restore(e.id)} style={smallBtn}>Restore</button>
        : <button onClick={() => dismiss(e.id)} style={smallBtn} title="Hide this alert. It's kept on record — restore it any time from “Show dismissed”.">Dismiss</button>}
    </div>
  )

  const isWarn = active.length > 0
  return (
    <div style={{ background: isWarn ? '#fffbeb' : '#f8fafc', border: `1px solid ${isWarn ? '#fcd34d' : '#e2e8f0'}`, borderRadius: 8, padding: 14, marginBottom: 16 }}>
      {isWarn ? (
        <>
          <div style={{ fontSize: 14, fontWeight: 700, color: '#92400e', marginBottom: 8 }}>
            ⚠ {active.length} course{active.length === 1 ? '' : 's'} returned a $0 / unpriced round — these rounds are hidden until fixed
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {active.map(e => rowFor(e, false))}
          </div>
        </>
      ) : (
        <div style={{ fontSize: 13, fontWeight: 600, color: '#166534' }}>✓ No active $0 courses.</div>
      )}

      {dismissed.length > 0 && (
        <div style={{ marginTop: isWarn ? 10 : 6 }}>
          <button onClick={() => setShowDismissed(s => !s)} style={{ ...smallBtn, background: 'transparent' }}>
            {showDismissed ? 'Hide' : 'Show'} {dismissed.length} dismissed
          </button>
          {showDismissed && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 8 }}>
              {dismissed.map(e => rowFor(e, true))}
            </div>
          )}
        </div>
      )}
    </div>
  )
}

// ── Duplicate-course finder ────────────────────────────────────────────────
// Clusters courses that share a normalized name (same club catalogued twice)
// so an admin can RETIRE the extras. Retiring sets courses.ArchivedAt — it
// hides the course from resellers + the catalog + the tee-cache, but keeps the
// row and its historical bookings intact, and is fully reversible. Lazy: the
// scan only runs when the panel is opened.
function DupChip({ label, value, tone }) {
  if (!value) return null
  const tones = {
    warn:    { bg: '#fef3c7', fg: '#92400e', bd: '#fcd34d' },
    good:    { bg: '#dcfce7', fg: '#166534', bd: '#86efac' },
    neutral: { bg: '#f1f5f9', fg: '#475569', bd: '#e2e8f0' },
  }
  const t = tones[tone] || tones.neutral
  return (
    <span style={{ fontSize: 11, fontWeight: 600, background: t.bg, color: t.fg, border: `1px solid ${t.bd}`, borderRadius: 10, padding: '1px 8px', whiteSpace: 'nowrap' }}>
      {label}{value === true ? '' : `: ${value}`}
    </span>
  )
}

function DuplicatesPanel({ onManage, onChanged }) {
  const [data, setData] = useState(null)      // { clusters, clusterCount }
  const [loading, setLoading] = useState(false)
  const [open, setOpen] = useState(false)
  const [busyId, setBusyId] = useState(null)

  function load() {
    setLoading(true)
    tournamentApi.get('/admin/course-integrations/duplicates')
      .then(r => setData(r.data))
      .catch(e => alert(e?.response?.data?.message || e.message))
      .finally(() => setLoading(false))
  }
  function toggle() {
    const next = !open
    setOpen(next)
    if (next && !data && !loading) load()
  }
  async function archive(c) {
    const warn = (c.bookingRounds > 0 || c.activeResellers > 0)
      ? `\n\nHEADS UP: this record has ${c.bookingRounds} booked round(s) and ${c.activeResellers} attached reseller(s). Retiring hides it from resellers going forward — existing bookings are untouched. Make sure you're keeping the OTHER record in this cluster.`
      : ''
    if (!confirm(`Retire "${c.courseName}" (#${c.courseId})?\n\nIt will be hidden from resellers, the catalog, and the tee-cache — but the record and its bookings are preserved. You can restore it any time.${warn}`)) return
    setBusyId(c.courseId)
    try { await tournamentApi.post(`/admin/course-integrations/courses/${c.courseId}/archive`); load(); onChanged && onChanged() }
    catch (e) { alert(e?.response?.data?.message || e.message) }
    finally { setBusyId(null) }
  }
  async function unarchive(c) {
    setBusyId(c.courseId)
    try { await tournamentApi.post(`/admin/course-integrations/courses/${c.courseId}/unarchive`); load(); onChanged && onChanged() }
    catch (e) { alert(e?.response?.data?.message || e.message) }
    finally { setBusyId(null) }
  }

  const clusters = data?.clusters || []

  const courseRow = (c) => (
    <div key={c.courseId} style={{
      display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap',
      background: c.archived ? '#f8fafc' : '#fff',
      border: `1px solid ${c.suggestKeep ? '#86efac' : '#e2e8f0'}`,
      borderRadius: 6, padding: '8px 10px', opacity: c.archived ? 0.7 : 1,
    }}>
      <div style={{ minWidth: 0, flex: '1 1 300px' }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: '#0f172a', display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
          {c.courseName} <span style={{ color: '#94a3b8', fontWeight: 400 }}>#{c.courseId}</span>
          {c.suggestKeep && <span style={{ fontSize: 11, fontWeight: 700, background: '#dcfce7', color: '#166534', border: '1px solid #86efac', borderRadius: 10, padding: '1px 8px' }}>★ Recommended keep</span>}
          {c.archived && <span style={{ fontSize: 11, fontWeight: 700, background: '#e2e8f0', color: '#475569', borderRadius: 10, padding: '1px 8px' }}>Retired</span>}
        </div>
        <div style={{ fontSize: 12, color: '#64748b', marginTop: 3, display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
          {c.city && <span>{c.city}</span>}
          <DupChip label="integration" value={c.activeIntegration} tone={c.activeIntegration ? 'good' : 'neutral'} />
          <DupChip label="resellers" value={c.activeResellers} tone={c.activeResellers > 0 ? 'warn' : 'neutral'} />
          <DupChip label="booked rounds" value={c.bookingRounds} tone={c.bookingRounds > 0 ? 'warn' : 'neutral'} />
          <DupChip label="packages" value={c.packageCount} tone={c.packageCount > 0 ? 'warn' : 'neutral'} />
          <DupChip label="cache cells" value={c.cacheCells} tone="neutral" />
          {c.hasImage && <DupChip label="image" value={true} tone="good" />}
          {c.isZeroPrice && <DupChip label="$0 unpriced" value={true} tone="warn" />}
        </div>
      </div>
      <button onClick={() => onManage({ courseId: c.courseId, courseName: c.courseName })} style={smallBtn}>Manage →</button>
      {c.archived
        ? <button onClick={() => unarchive(c)} disabled={busyId === c.courseId} style={{ ...smallBtn, background: '#0f172a', color: '#fff', borderColor: '#0f172a' }}>{busyId === c.courseId ? '…' : 'Restore'}</button>
        : <button onClick={() => archive(c)} disabled={busyId === c.courseId} style={{ ...smallBtn, background: '#fff', color: '#b91c1c', borderColor: '#fca5a5' }} title="Retire this duplicate (reversible)">{busyId === c.courseId ? '…' : 'Retire'}</button>}
    </div>
  )

  return (
    <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, padding: 14, marginBottom: 16 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <button onClick={toggle} style={{ ...smallBtn, background: 'transparent', fontWeight: 700, fontSize: 14, color: '#0f172a' }}>
          {open ? '▾' : '▸'} 🔁 Duplicate courses
        </button>
        {data && <span style={{ fontSize: 13, color: data.clusterCount > 0 ? '#92400e' : '#166534', fontWeight: 600 }}>
          {data.clusterCount > 0 ? `${data.clusterCount} likely-duplicate cluster${data.clusterCount === 1 ? '' : 's'} found` : '✓ No duplicate clusters found'}
        </span>}
        {open && data && <button onClick={load} disabled={loading} style={{ ...smallBtn, marginLeft: 'auto' }}>{loading ? 'Scanning…' : 'Re-scan'}</button>}
      </div>

      {open && (
        <div style={{ marginTop: 12 }}>
          {loading && !data ? (
            <div style={{ fontSize: 13, color: '#64748b', padding: '8px 2px' }}>Scanning the catalog…</div>
          ) : clusters.length === 0 ? (
            <div style={{ fontSize: 13, color: '#64748b', padding: '8px 2px' }}>
              No clusters. Courses are grouped by a normalized name (generic “Golf Club / Course / Country Club” words stripped, distinguishing words like North/South kept), so genuine multi-course clubs aren’t flagged.
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              <div style={{ fontSize: 12, color: '#64748b' }}>
                Each cluster is the same club catalogued more than once. Keep the ★ record (richest footprint) and <b>Retire</b> the others. Retiring is reversible and never deletes bookings.
              </div>
              {clusters.map(cl => (
                <div key={cl.key} style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8, padding: 10 }}>
                  <div style={{ fontSize: 12, fontWeight: 700, color: '#475569', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 8 }}>
                    “{cl.key}” · {cl.count} records{cl.activeCount !== cl.count ? ` · ${cl.activeCount} active` : ''}
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    {cl.courses.map(courseRow)}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  )
}

// Which credential fields the editor shows per provider.
const CONFIG_FIELDS_BY_PROVIDER = {
  'teeon-gn':      ['courseCode', 'facilityToken', 'baseUrl', 'apiKey'],
  'teeon-classic': ['courseCode', 'accessToken', 'applicationKey', 'baseUrl'],
}

function IntegrationCard({ intg, onChanged }) {
  const [editing, setEditing] = useState(false)
  const [busy, setBusy] = useState(false)
  const [form, setForm] = useState({})
  const [feeVal, setFeeVal] = useState(intg.fallbackGreenFee != null ? String(intg.fallbackGreenFee) : '')
  const [feeBusy, setFeeBusy] = useState(false)

  const fields = CONFIG_FIELDS_BY_PROVIDER[intg.provider] || ['courseCode', 'baseUrl', 'apiKey']
  const cfgLines = Object.entries(intg.config).filter(([, v]) => v != null && v !== '')
  const isGolf = intg.category === 'golf_booking'

  async function saveFee(clear) {
    setFeeBusy(true)
    try {
      const body = { fallbackGreenFee: clear ? null : (feeVal.trim() === '' ? null : Number(feeVal)) }
      await tournamentApi.put(`/admin/course-integrations/integrations/${intg.id}/fallback-fee`, body)
      if (clear) setFeeVal('')
      onChanged()
    } catch (e) { alert(e?.response?.data?.message || e.message) }
    finally { setFeeBusy(false) }
  }

  async function toggle(next) {
    setBusy(true)
    try { await tournamentApi.patch(`/admin/course-integrations/integrations/${intg.id}/active`, { isActive: next }); onChanged() }
    catch (e) { alert(e?.response?.data?.message || e.message) }
    finally { setBusy(false) }
  }
  async function remove() {
    if (!confirm(`Permanently delete this ${intg.provider} integration (#${intg.id})? This cannot be undone. Use it to clean up a bogus or duplicate integration.`)) return
    setBusy(true)
    try { await tournamentApi.delete(`/admin/course-integrations/integrations/${intg.id}`); onChanged() }
    catch (e) { alert(e?.response?.data?.message || e.message) }
    finally { setBusy(false) }
  }
  async function save() {
    const config = {}
    for (const k of fields) { const v = (form[k] || '').trim(); if (v) config[k] = v }
    if (!Object.keys(config).length) { setEditing(false); return }
    setBusy(true)
    try { await tournamentApi.put(`/admin/course-integrations/integrations/${intg.id}/config`, { config }); setEditing(false); setForm({}); onChanged() }
    catch (e) { alert(e?.response?.data?.message || e.message) }
    finally { setBusy(false) }
  }

  return (
    <div style={{ border: '1px solid ' + (intg.isActive ? '#86efac' : '#e2e8f0'), borderRadius: 8, padding: 12, background: intg.isActive ? '#f0fdf4' : '#fff' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
        <div>
          <strong style={{ fontSize: 14 }}>{intg.provider}</strong>
          <span style={{ color: '#94a3b8', fontSize: 12, marginLeft: 6 }}>#{intg.id} · {intg.category}</span>
          {intg.providerClassName && <span style={{ color: '#64748b', fontSize: 12, marginLeft: 6 }}>({intg.providerClassName})</span>}
        </div>
        <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13, cursor: 'pointer' }}>
          <input type="checkbox" checked={intg.isActive} disabled={busy} onChange={e => toggle(e.target.checked)} />
          {intg.isActive ? 'Active' : 'Off'}
        </label>
      </div>
      <div style={{ marginTop: 8, fontSize: 12, color: '#475569', display: 'flex', flexDirection: 'column', gap: 2 }}>
        {cfgLines.length === 0 && (
          intg.hasEffectiveCredentials
            ? <div style={{ color: '#16a34a', fontWeight: 600 }}>
                ✓ Credentials configured{intg.credentialsSource === 'course_integolf_products' ? ' via setup wizard' : ''}
                {intg.effectiveCourseCode ? ` (course code ${intg.effectiveCourseCode})` : ''}.
              </div>
            : <div style={{ color: '#b45309', fontWeight: 600 }}>⚠ No credentials set.</div>
        )}
        {cfgLines.map(([k, v]) => (
          <div key={k}>{k}: <code style={{ color: '#0f172a' }}>{typeof v === 'object' ? JSON.stringify(v) : String(v)}</code></div>
        ))}
      </div>
      {!editing ? (
        <div style={{ display: 'flex', gap: 8, marginTop: 8, alignItems: 'center' }}>
          <button onClick={() => setEditing(true)} style={smallBtn}>Edit credentials</button>
          <button onClick={remove} disabled={busy} style={{ ...smallBtn, color: '#b91c1c', borderColor: '#fecaca', marginLeft: 'auto' }}>Delete integration</button>
        </div>
      ) : (
        <div style={{ marginTop: 10, display: 'flex', flexDirection: 'column', gap: 6 }}>
          {fields.map(k => (
            <label key={k} style={{ fontSize: 12, color: '#475569', display: 'flex', flexDirection: 'column', gap: 2 }}>
              {k}
              <input
                type="text"
                value={form[k] || ''}
                onChange={e => setForm(f => ({ ...f, [k]: e.target.value }))}
                placeholder={k === 'baseUrl' ? 'https://api.teeon.com' : ''}
                style={{ padding: '6px 8px', border: '1px solid #cbd5e1', borderRadius: 4, fontSize: 12, fontFamily: 'monospace' }}
              />
            </label>
          ))}
          <div style={{ fontSize: 11, color: '#94a3b8' }}>Only filled fields are saved; blank fields keep their current value.</div>
          <div style={{ display: 'flex', gap: 6 }}>
            <button onClick={save} disabled={busy} style={{ ...smallBtn, background: '#0f172a', color: '#fff', borderColor: '#0f172a' }}>{busy ? 'Saving…' : 'Save'}</button>
            <button onClick={() => { setEditing(false); setForm({}) }} style={smallBtn}>Cancel</button>
          </div>
        </div>
      )}

      {/* Flat green fee — used when the live tee sheet returns a $0/blank fee
          so the round is still sellable (flat fee + reseller markup) instead
          of being hidden. Only meaningful for golf-booking integrations. */}
      {isGolf && (
        <div style={{ marginTop: 10, paddingTop: 10, borderTop: '1px solid #e2e8f0' }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: '#475569', marginBottom: 4 }}>
            Flat green fee <span style={{ fontWeight: 400, color: '#94a3b8' }}>— used if this course returns $0</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
            <span style={{ color: '#64748b', fontSize: 13 }}>$</span>
            <input
              type="number" min="0" step="0.01" inputMode="decimal"
              value={feeVal}
              onChange={e => setFeeVal(e.target.value)}
              placeholder="none"
              style={{ width: 100, padding: '6px 8px', border: '1px solid #cbd5e1', borderRadius: 4, fontSize: 13 }}
            />
            <button onClick={() => saveFee(false)} disabled={feeBusy} style={{ ...smallBtn, background: '#0f172a', color: '#fff', borderColor: '#0f172a' }}>{feeBusy ? 'Saving…' : 'Save fee'}</button>
            {intg.fallbackGreenFee != null && (
              <button onClick={() => saveFee(true)} disabled={feeBusy} style={smallBtn}>Clear</button>
            )}
            {intg.fallbackGreenFee != null && (
              <span style={{ fontSize: 12, color: '#16a34a', fontWeight: 600 }}>Active: ${Number(intg.fallbackGreenFee).toFixed(2)}</span>
            )}
          </div>
          <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 4 }}>
            Leave blank to hide $0 rounds instead. The reseller's golf markup still applies on top.
          </div>
        </div>
      )}
    </div>
  )
}

/**
 * GolfTrailImagePanel — configure the Golf Trail course-image export token and
 * trigger a mirror-sync on demand. The daily cron does the same automatically;
 * this is for setup + "pull now" after a change. Manual per-course photos are
 * never overwritten by the sync (manual wins).
 */
function GolfTrailImagePanel({ onSynced }) {
  const [status, setStatus] = useState(null)
  const [token, setToken] = useState('')
  const [baseUrl, setBaseUrl] = useState('')
  const [busy, setBusy] = useState(false)
  const [msg, setMsg] = useState(null)
  const [err, setErr] = useState(null)
  const [open, setOpen] = useState(false)

  const load = () => {
    tournamentApi.get('/admin/course-integrations/golftrail-images/status')
      .then(({ data }) => { setStatus(data); setBaseUrl(data.baseUrl || '') })
      .catch(() => {})
  }
  useEffect(load, [])

  async function saveConfig() {
    setBusy(true); setErr(null); setMsg(null)
    try {
      const body = { baseUrl: baseUrl || undefined }
      if (token) body.token = token
      const { data } = await tournamentApi.put('/admin/course-integrations/golftrail-images/config', body)
      setStatus(data.status); setToken(''); setMsg('Saved.')
    } catch (e) { setErr(e?.response?.data?.message || e.message) }
    finally { setBusy(false) }
  }

  async function syncNow(full) {
    setBusy(true); setErr(null); setMsg(null)
    try {
      const { data } = await tournamentApi.post('/admin/course-integrations/golftrail-images/sync', { full: !!full })
      const s = data.stats
      setMsg(`Synced: ${s.imagesDownloaded} downloaded, ${s.imagesSkipped} unchanged, ${s.imagesPruned} pruned across ${s.coursesProcessed} course(s).${s.errors?.length ? ` ${s.errors.length} error(s).` : ''}`)
      load(); onSynced && onSynced()
    } catch (e) { setErr(e?.response?.data?.message || e.message) }
    finally { setBusy(false) }
  }

  const configured = status?.configured
  const lastRun = status?.lastRun
  return (
    <div style={{ margin: '0 0 16px', background: '#fff', border: '1px solid #e2e8f0', borderRadius: 10, overflow: 'hidden' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', cursor: 'pointer', background: '#f8fafc' }} onClick={() => setOpen(o => !o)}>
        <span style={{ fontSize: 14 }}>🖼️</span>
        <span style={{ fontWeight: 700, fontSize: 13.5, color: '#0f172a' }}>Golf Trail course images</span>
        <span style={{ fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 999, background: configured ? '#dcfce7' : '#f1f5f9', color: configured ? '#166534' : '#64748b' }}>
          {configured ? 'Connected' : 'Not configured'}
        </span>
        {status?.totalImages > 0 && (
          <span style={{ fontSize: 12, color: '#64748b' }}>{status.totalImages} images · {status.coursesWithImages} courses</span>
        )}
        <span style={{ marginLeft: 'auto', fontSize: 12, color: '#94a3b8' }}>{open ? '▲' : '▼'}</span>
      </div>
      {open && (
        <div style={{ padding: 14, display: 'grid', gap: 12 }}>
          <div style={{ fontSize: 12.5, color: '#64748b', lineHeight: 1.5, maxWidth: 720 }}>
            Pulls cover + gallery photos from Golf Trail's export feed and mirrors them into our own storage,
            matched to courses by ID. Runs automatically once a day; use <b>Sync now</b> after provisioning or rotating the token.
            A photo you set by hand on a course is never overwritten.
          </div>
          {err && <div style={errorBoxStyle}>{err}</div>}
          {msg && <div style={{ padding: '8px 12px', background: '#ecfdf5', border: '1px solid #a7f3d0', borderRadius: 8, fontSize: 12.5, color: '#065f46' }}>{msg}</div>}

          <div style={{ display: 'grid', gap: 8, maxWidth: 560 }}>
            <label style={{ fontSize: 12, color: '#64748b', fontWeight: 600 }}>Feed URL
              <input type="url" value={baseUrl} onChange={e => setBaseUrl(e.target.value)}
                placeholder="https://api.golftrail.ca/api/exports/integolf/course-images"
                style={{ ...inputStyle, width: '100%', marginTop: 4 }} /></label>
            <label style={{ fontSize: 12, color: '#64748b', fontWeight: 600 }}>Bearer token
              <input type="password" value={token} onChange={e => setToken(e.target.value)}
                placeholder={status?.tokenMasked ? `current: ${status.tokenMasked}` : 'paste the token from Golf Trail'}
                style={{ ...inputStyle, width: '100%', marginTop: 4 }} autoComplete="off" /></label>
            <div style={{ fontSize: 11, color: '#94a3b8' }}>Leave the token blank to keep the current one. The token is stored server-side and never shown again.</div>
          </div>

          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <button onClick={saveConfig} disabled={busy} style={{ padding: '8px 16px', background: '#0f172a', color: '#fff', border: 'none', borderRadius: 6, fontSize: 13, fontWeight: 600, cursor: 'pointer', opacity: busy ? 0.5 : 1 }}>Save settings</button>
            <button onClick={() => syncNow(false)} disabled={busy || !configured} title={configured ? 'Pull changed courses now' : 'Configure the token first'} style={{ padding: '8px 16px', background: '#00d19f', color: '#fff', border: 'none', borderRadius: 6, fontSize: 13, fontWeight: 600, cursor: configured ? 'pointer' : 'not-allowed', opacity: (busy || !configured) ? 0.5 : 1 }}>{busy ? 'Working…' : 'Sync now'}</button>
            <button onClick={() => syncNow(true)} disabled={busy || !configured} title="Re-check every course, ignoring the delta timestamp" style={{ ...smallBtn, opacity: (busy || !configured) ? 0.5 : 1 }}>Full re-sync</button>
            {lastRun && (
              <span style={{ fontSize: 11.5, color: '#94a3b8' }}>
                Last run: {new Date(lastRun.finishedAt).toLocaleString()} · {lastRun.imagesDownloaded} downloaded{lastRun.errorCount ? ` · ${lastRun.errorCount} err` : ''}
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

/**
 * LocationModal — admin sets/edits city + map coordinates for a course so
 * resellers' radius-based grouping queries pick it up. Catalog imports
 * (TTC + JSON) often arrive with no coords, hence this surface.
 */
function LocationModal({ row, onClose, onSaved }) {
  const [city, setCity] = useState(row.city || '')
  const [lat, setLat] = useState(row.latitude ?? null)
  const [lng, setLng] = useState(row.longitude ?? null)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)

  async function save() {
    if (lat == null || lng == null) {
      setError('Drop a pin on the map (or search for the course) before saving.')
      return
    }
    setSaving(true); setError(null)
    try {
      await tournamentApi.patch(`/admin/course-integrations/courses/${row.courseId}/location`, {
        city: city || null,
        latitude: lat,
        longitude: lng,
      })
      onSaved()
    } catch (err) {
      setError(err?.response?.data?.message || err.message)
      setSaving(false)
    }
  }

  return (
    <div style={modalOverlay} onClick={onClose}>
      <div style={{ ...modalBody, maxWidth: 720 }} onClick={(e) => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 }}>
          <div>
            <h2 style={{ margin: 0, fontSize: 18, color: '#0f172a' }}>Set location</h2>
            <div style={{ color: '#64748b', fontSize: 13, marginTop: 4 }}>{row.courseName}</div>
          </div>
          <button onClick={onClose} style={closeBtn}>×</button>
        </div>

        {error && <div style={errorBoxStyle}>{error}</div>}

        <div style={{ marginBottom: 12 }}>
          <label style={{ display: 'block', fontSize: 12, color: '#64748b', fontWeight: 600, marginBottom: 4, textTransform: 'uppercase', letterSpacing: 0.5 }}>
            City (free text — shown to resellers)
          </label>
          <input
            type="text"
            value={city}
            onChange={(e) => setCity(e.target.value)}
            placeholder="e.g. Sydney, Nova Scotia"
            style={{ ...inputStyle, width: '100%' }}
          />
        </div>

        <div style={{ marginBottom: 12 }}>
          <label style={{ display: 'block', fontSize: 12, color: '#64748b', fontWeight: 600, marginBottom: 4, textTransform: 'uppercase', letterSpacing: 0.5 }}>
            Map pin
          </label>
          <div style={{ fontSize: 12, color: '#94a3b8', marginBottom: 8 }}>
            Drop the pin on the course's clubhouse. Used by resellers' grouping radius queries.
          </div>
          <MapPicker
            lat={lat}
            lng={lng}
            // City wins as the search query when the admin types one --
            // it's usually a real address or place name. Falls back to
            // the course name only when City is empty (course name is
            // a poor geocoder input).
            searchQuery={city.trim() || row.courseName}
            height={340}
            onChange={({ lat: newLat, lng: newLng, address }) => {
              setLat(newLat); setLng(newLng)
              // MapPicker passes `address` as an object — pull out a printable
              // string. Don't overwrite the city if the admin already set one.
              if (!city && address) {
                const str = typeof address === 'string'
                  ? address
                  : (address.displayName || address.address?.line1 || '')
                if (str) setCity(str)
              }
            }}
          />
          {lat != null && lng != null && (
            <div style={{ fontSize: 11, color: '#64748b', marginTop: 6, fontFamily: 'monospace' }}>
              {Number(lat).toFixed(5)}, {Number(lng).toFixed(5)}
            </div>
          )}
        </div>

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
          <button onClick={onClose} style={smallBtn}>Cancel</button>
          <button
            onClick={save}
            disabled={saving}
            style={{ padding: '8px 16px', background: '#00d19f', color: '#fff', border: 'none', borderRadius: 6, fontSize: 14, fontWeight: 600, cursor: 'pointer', opacity: saving ? 0.5 : 1 }}
          >{saving ? 'Saving…' : 'Save location'}</button>
        </div>
      </div>
    </div>
  )
}

/**
 * ImageModal — admin sets/clears a scenic hero photo URL for a course.
 * Courses otherwise only carry a logo; this photo is shown on the customer
 * trip page golf rows (prefers this over the logo when present).
 */
// ─── Rate picker modal ───────────────────────────────────────────────────
function RatePickerModal({ row, onClose, onSaved }) {
  const [rates, setRates] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [saving, setSaving] = useState(false)
  const [filter, setFilter] = useState('')
  const [showAll, setShowAll] = useState(false)

  useEffect(() => {
    setLoading(true); setError(null)
    const url = row.isVirtual
      ? `/admin/course-integrations/ttc-catalog-rates?courseCode=${encodeURIComponent(row.ttcCourseCode)}&teesheet=${encodeURIComponent(row.ttcTeesheet)}`
      : `/admin/course-integrations/${row.integration.id}/rates`
    tournamentApi.get(url)
      .then(r => setRates(r.data))
      .catch(err => setError(err?.response?.data?.message || err.message))
      .finally(() => setLoading(false))
  }, [row.isVirtual, row.ttcCourseCode, row.ttcTeesheet, row.integration?.id])

  async function applyRate(rate) {
    setSaving(true)
    try {
      if (row.isVirtual) {
        await tournamentApi.post('/admin/course-integrations/import-ttc', {
          ttcCourseCode: row.ttcCourseCode,
          ttcTeesheet:   row.ttcTeesheet,
          rateCode:      rate.rateCode,
          rateName:      rate.rateName,
        })
      } else {
        await tournamentApi.put(`/admin/course-integrations/${row.integration.id}/rate`, {
          rateCode: rate.rateCode,
          rateName: rate.rateName,
        })
      }
      onSaved()
    } catch (err) {
      alert(err?.response?.data?.message || err.message)
      setSaving(false)
    }
  }

  const filtered = useMemo(() => {
    if (!rates?.rates) return []
    let arr = rates.rates
    if (!showAll) arr = arr.filter(r => r.isLikelyPublic || r.isCurrent || r.isSuggested)
    if (filter) {
      const f = filter.toLowerCase()
      arr = arr.filter(r => r.rateName?.toLowerCase().includes(f) || r.rateCode?.toLowerCase().includes(f))
    }
    return arr
  }, [rates, filter, showAll])

  return (
    <div style={modalOverlay} onClick={onClose}>
      <div style={modalBody} onClick={(e) => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 }}>
          <div>
            <h2 style={{ margin: 0, fontSize: 18, color: '#0f172a' }}>
              {row.isVirtual ? 'Import course & set rate' : 'Edit course'}
            </h2>
            <div style={{ color: '#64748b', fontSize: 13, marginTop: 4 }}>
              {row.courseName} · {row.integration.provider}
              {row.isVirtual && row.city ? ` · ${row.city}` : ''}
            </div>
          </div>
          <button onClick={onClose} style={closeBtn}>×</button>
        </div>

        {error && <div style={errorBoxStyle}>{error}</div>}

        {rates?.provider && rates.provider !== 'ttc' ? (
          <div style={infoBoxStyle}>
            Live rate picker isn't wired for <code>{rates.provider}</code> yet.
            Edit the credentials directly in <Link to={`/admin/course-integrations/${rates.provider}`} style={linkStyle}>{rates.provider}</Link>.
          </div>
        ) : loading ? (
          <div style={{ padding: 40, textAlign: 'center', color: '#64748b' }}>Loading rates…</div>
        ) : !rates?.rates?.length ? (
          <div style={{ padding: 40, textAlign: 'center', color: '#64748b' }}>No rates returned by provider.</div>
        ) : (
          <>
            <div style={{ fontSize: 13, color: '#64748b', marginBottom: 12 }}>Pick the default rate for this course:</div>
            <div style={{ display: 'flex', gap: 12, marginBottom: 12, alignItems: 'center' }}>
              <input placeholder="Filter rates…" value={filter} onChange={(e) => setFilter(e.target.value)} style={{ ...inputStyle, flex: 1 }} />
              <Toggle
                checked={showAll}
                onChange={setShowAll}
                size="sm"
                label={`Show non-public (${rates.rates.length} total)`}
              />
            </div>
            <div style={{ maxHeight: 420, overflow: 'auto', border: '1px solid #e2e8f0', borderRadius: 6 }}>
              {filtered.map(rate => (
                <div key={rate.rateCode} style={{
                  padding: '10px 14px', borderBottom: '1px solid #f1f5f9',
                  display: 'flex', alignItems: 'center', gap: 12,
                  background: rate.isCurrent ? '#f0fdfa' : 'transparent',
                }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 14, color: '#0f172a' }}>
                      {rate.isLikelyPublic && <span style={{ color: '#f59e0b', marginRight: 4 }}>★</span>}
                      {rate.rateName || '(no name)'}
                      {rate.isCurrent && <span style={{ ...pill('#dcfce7', '#166534'), marginLeft: 8 }}>Current</span>}
                      {rate.isSuggested && !rate.isCurrent && <span style={{ ...pill('#fef3c7', '#92400e'), marginLeft: 8 }}>Suggested</span>}
                    </div>
                    <code style={{ fontSize: 11, color: '#94a3b8' }}>{rate.rateCode}</code>
                  </div>
                  {!rate.isCurrent && (
                    <button onClick={() => applyRate(rate)} disabled={saving} style={smallBtn}>
                      {saving ? 'Saving…' : 'Use this'}
                    </button>
                  )}
                </div>
              ))}
              {filtered.length === 0 && (
                <div style={{ padding: 20, textAlign: 'center', color: '#94a3b8', fontSize: 13 }}>
                  No matches. Try clearing the filter or checking "Show non-public".
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  )
}

// ─── Styles ──────────────────────────────────────────────────────────────
const inputStyle = { padding: '8px 12px', border: '1px solid #cbd5e1', borderRadius: 6, fontSize: 14, background: '#fff', color: '#0f172a' }
const primaryBtn = { padding: '8px 16px', background: '#00d19f', color: '#fff', border: 'none', borderRadius: 6, fontSize: 14, fontWeight: 600, cursor: 'pointer' }
const smallBtn = { padding: '4px 12px', background: '#fff', color: '#0f172a', border: '1px solid #cbd5e1', borderRadius: 4, fontSize: 12, fontWeight: 500, cursor: 'pointer' }
const closeBtn = { background: 'transparent', border: 'none', fontSize: 24, color: '#64748b', cursor: 'pointer', lineHeight: 1, padding: 0 }
const errorBoxStyle = { background: '#fee2e2', color: '#991b1b', padding: '12px 16px', borderRadius: 6, marginBottom: 16, fontSize: 14 }
const infoBoxStyle = { background: '#eff6ff', color: '#1e40af', padding: '10px 14px', borderRadius: 6, marginBottom: 12, fontSize: 13, lineHeight: 1.5 }
const linkStyle = { color: '#00d19f', textDecoration: 'none' }
const modalOverlay = { position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 20 }
const modalBody = { background: '#fff', borderRadius: 10, padding: 24, maxWidth: 720, width: '100%', maxHeight: '90vh', overflow: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.25)' }
function pill(bg, fg) {
  return { display: 'inline-block', padding: '2px 8px', background: bg, color: fg, borderRadius: 12, fontSize: 11, fontWeight: 600 }
}
/**
 * MarkupInput — inline editable golf markup % per course. Saves on blur
 * via PATCH /admin/course-integrations/courses/:id/markup. Default rendered
 * is 10% (no DB row); a green dot indicates a custom override is in effect
 * so admin can see at a glance which courses have negotiated rates.
 */
function MarkupInput({ coursesId, value, custom, onChanged }) {
  const initialPct = ((Number(value) || 0) * 100).toFixed(2).replace(/\.?0+$/, '')
  const [text, setText] = useState(initialPct)
  const [busy, setBusy] = useState(false)

  // Sync if parent value changes externally (e.g. reload).
  useEffect(() => { setText(((Number(value) || 0) * 100).toFixed(2).replace(/\.?0+$/, '')) }, [value])

  async function commit() {
    const pct = Number(text)
    if (!Number.isFinite(pct) || pct < 0 || pct > 100) {
      setText(initialPct)
      return
    }
    const decimal = Math.round(pct * 1000) / 100000 // pct/100 with 5dp precision
    if (decimal === Number(value)) return
    setBusy(true)
    try {
      await tournamentApi.patch(`/admin/course-integrations/courses/${coursesId}/markup`, {
        golfMarkupPct: decimal,
      })
      onChanged(decimal)
    } catch (err) {
      alert(err?.response?.data?.message || err.message)
      setText(initialPct)
    } finally { setBusy(false) }
  }

  return (
    <div style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
      <span
        title={custom ? 'Custom markup set' : 'Using 10% default'}
        style={{
          display: 'inline-block', width: 6, height: 6, borderRadius: 3,
          background: custom ? '#00d19f' : '#e2e8f0',
        }}
      />
      <input
        type="number" min={0} max={100} step="0.01"
        value={text}
        onChange={e => setText(e.target.value)}
        onBlur={commit}
        disabled={busy}
        style={{
          width: 56, padding: '4px 6px',
          border: '1px solid #cbd5e1', borderRadius: 4,
          fontSize: 13, textAlign: 'right', fontFamily: 'inherit',
          background: busy ? '#f8fafc' : '#fff',
        }}
      />
      <span style={{ fontSize: 12, color: '#64748b' }}>%</span>
    </div>
  )
}

function ActiveToggle({ coursesId, active, disabled, onChange }) {
  const [busy, setBusy] = useState(false)
  async function flip(next) {
    if (busy || disabled) return
    setBusy(true)
    onChange(next) // optimistic
    try {
      await tournamentApi.patch(`/admin/course-integrations/courses/${coursesId}/active`, {
        isResellerActive: next,
      })
    } catch (err) {
      onChange(active) // rollback
      alert(err?.response?.data?.message || err.message)
    } finally { setBusy(false) }
  }
  return <Toggle checked={active} onChange={flip} disabled={disabled || busy} />
}

/**
 * Pager — page-size selector + prev/next + condensed page numbers. Lives in
 * the table footer; the parent owns `page` + `pageSize` state and does the
 * slicing. Renders nothing extra if everything fits in one page so the UI
 * stays clean when filters narrow the list down.
 */
function Pager({ startIdx, endIdx, total, page, totalPages, pageSize, onPageChange, onPageSizeChange }) {
  const { t } = useLanguage()
  // Build a condensed page-number list: always show first, last, current ±1,
  // with "…" gaps. Avoids 50 buttons when there are 500 courses.
  function pageNumbers() {
    if (totalPages <= 7) return Array.from({ length: totalPages }, (_, i) => i + 1)
    const out = new Set([1, totalPages, page, page - 1, page + 1])
    const sorted = Array.from(out).filter(n => n >= 1 && n <= totalPages).sort((a, b) => a - b)
    const result = []
    for (let i = 0; i < sorted.length; i++) {
      if (i > 0 && sorted[i] - sorted[i - 1] > 1) result.push('…')
      result.push(sorted[i])
    }
    return result
  }
  const showPager = pageSize !== 'all' && totalPages > 1
  const shownStart = total === 0 ? 0 : startIdx
  return (
    <div style={{
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      gap: 12, padding: '10px 14px', borderTop: '1px solid #f1f5f9',
      background: '#f8fafc', fontSize: 13, color: '#64748b', flexWrap: 'wrap',
    }}>
      <div>
        {/* "Showing X–Y of Z" — splitting around bold numbers since the
            translated phrase may put parts in a different order. */}
        {t('sp_pager_showing_lower', 'Showing')} <b>{shownStart}</b>–<b>{endIdx}</b> {t('sp_pager_of_lower', 'of')} <b>{total}</b>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          {t('sp_pager_per_page', 'Per page:')}
          <select
            value={String(pageSize)}
            onChange={e => onPageSizeChange(e.target.value === 'all' ? 'all' : Number(e.target.value))}
            style={{ padding: '4px 8px', border: '1px solid #cbd5e1', borderRadius: 4, fontSize: 13, background: '#fff', color: '#0f172a' }}
          >
            <option value="10">10</option>
            <option value="25">25</option>
            <option value="50">50</option>
            <option value="100">100</option>
            <option value="all">{t('sp_pager_all', 'All')}</option>
          </select>
        </label>
        {showPager && (
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
            <PagerBtn onClick={() => onPageChange(page - 1)} disabled={page <= 1}>{t('sp_pager_prev', '‹ Prev')}</PagerBtn>
            {pageNumbers().map((n, i) => (
              n === '…'
                ? <span key={`gap-${i}`} style={{ padding: '0 6px', color: '#94a3b8' }}>…</span>
                : <PagerBtn key={n} active={n === page} onClick={() => onPageChange(n)}>{n}</PagerBtn>
            ))}
            <PagerBtn onClick={() => onPageChange(page + 1)} disabled={page >= totalPages}>{t('sp_pager_next', 'Next ›')}</PagerBtn>
          </div>
        )}
      </div>
    </div>
  )
}
function PagerBtn({ active, disabled, onClick, children }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      style={{
        minWidth: 30, padding: '4px 10px',
        border: active ? '1px solid #00d19f' : '1px solid #cbd5e1',
        background: active ? '#00d19f' : '#fff',
        color: active ? '#fff' : (disabled ? '#cbd5e1' : '#0f172a'),
        borderRadius: 4, fontSize: 13, fontWeight: 500,
        cursor: disabled ? 'not-allowed' : 'pointer',
      }}
    >{children}</button>
  )
}

// "Packages" cell: a count badge with a hover tooltip listing the reseller(s)
// and package name(s) the course is in. The tooltip is position:fixed (placed
// from the badge's viewport rect) so it isn't clipped by the table wrapper's
// overflow:hidden.
function PackagesCell({ count, packages }) {
  const [pos, setPos] = useState(null)
  if (!count) return <span style={{ color: '#cbd5e1' }}>0</span>

  const byReseller = {}
  for (const p of (packages || [])) {
    (byReseller[p.resellerName || '—'] = byReseller[p.resellerName || '—'] || []).push(p)
  }

  function enter(e) {
    const r = e.currentTarget.getBoundingClientRect()
    setPos({ x: r.left + r.width / 2, y: r.bottom + 6 })
  }

  return (
    <>
      <span
        onMouseEnter={enter}
        onMouseLeave={() => setPos(null)}
        style={{
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
          minWidth: 24, height: 22, padding: '0 8px', borderRadius: 999,
          background: '#eff6ff', color: '#1d4ed8', fontWeight: 700, fontSize: 13,
          border: '1px solid #bfdbfe', cursor: 'default',
        }}
      >{count}</span>
      {pos && (
        <div style={{
          position: 'fixed', left: pos.x, top: pos.y, transform: 'translateX(-50%)',
          zIndex: 1000, minWidth: 200, maxWidth: 320, textAlign: 'left',
          background: '#0f172a', color: '#e2e8f0', borderRadius: 8, padding: '10px 12px',
          boxShadow: '0 8px 24px rgba(15,23,42,0.28)', fontSize: 12, lineHeight: 1.5,
        }}>
          {Object.entries(byReseller).map(([reseller, pkgs]) => (
            <div key={reseller} style={{ marginBottom: 8 }}>
              <div style={{ fontWeight: 700, color: '#fff' }}>{reseller}</div>
              {pkgs.map(p => (
                <div key={p.packageId} style={{ color: '#cbd5e1', paddingLeft: 10 }}>
                  {p.packageName}
                  {p.status && p.status !== 'active' && (
                    <span style={{ color: '#94a3b8', fontStyle: 'italic' }}> ({p.status})</span>
                  )}
                </div>
              ))}
            </div>
          ))}
        </div>
      )}
    </>
  )
}

function Chip({ active, onClick, children }) {
  return (
    <button
      onClick={onClick}
      style={{
        padding: '6px 12px',
        borderRadius: 16,
        border: active ? '1px solid #00d19f' : '1px solid #cbd5e1',
        background: active ? '#00d19f' : '#fff',
        color: active ? '#fff' : '#0f172a',
        fontSize: 13, fontWeight: 500, cursor: 'pointer',
        transition: 'background .12s, border-color .12s',
      }}
    >{children}</button>
  )
}
function Th({ children, style }) {
  return <th style={{ padding: '10px 14px', textAlign: 'left', fontSize: 12, color: '#64748b', fontWeight: 600, ...style }}>{children}</th>
}
function Td({ children, style }) {
  return <td style={{ padding: '12px 14px', fontSize: 14, color: '#0f172a', verticalAlign: 'top', ...style }}>{children}</td>
}
